/**
 * The game plan (docs/game-design.md §8, D5): how a fighter's bot fights when they're away, when
 * an opponent fights their plan live, and when a bot stands in for them. How the plan drives the
 * bot: bot/bot.ts and docs/fight.md §8.
 */
import { DEFAULT_PLAN, type GamePlan, type PlanStyle, type RoundPlan, type WhenHurt } from '../bot/bot';
import { FIGHT } from '../config/fight';
import type { SkillRanks } from '../config/skills';

const STYLES: readonly PlanStyle[] = ['pressure', 'counter', 'grinder'];
const ROUNDS: readonly RoundPlan[] = ['feelOut', 'push', 'finish'];
const HURT: readonly WhenHurt[] = ['cover', 'swingBack'];

/** A clean plan from whatever a client sent; anything missing or invalid takes the default. */
export function normalizePlan(raw: unknown): GamePlan {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const style = STYLES.includes(o.style as PlanStyle) ? (o.style as PlanStyle) : DEFAULT_PLAN.style;
  const aggression =
    typeof o.aggression === 'number' && Number.isFinite(o.aggression) ? Math.max(0, Math.min(100, Math.round(o.aggression))) : DEFAULT_PLAN.aggression;
  const given = Array.isArray(o.rounds) ? o.rounds : [];
  const rounds = Array.from({ length: FIGHT.rounds }, (_, i) =>
    ROUNDS.includes(given[i] as RoundPlan) ? (given[i] as RoundPlan) : (DEFAULT_PLAN.rounds[i] ?? 'push'),
  );
  const whenHurt = HURT.includes(o.whenHurt as WhenHurt) ? (o.whenHurt as WhenHurt) : DEFAULT_PLAN.whenHurt;
  return { style, aggression, rounds, whenHurt };
}

export type AggressionLabel = 'patient' | 'measured' | 'aggressive' | 'reckless';

export function aggressionLabel(aggression: number): AggressionLabel {
  return aggression < 25 ? 'patient' : aggression < 50 ? 'measured' : aggression < 75 ? 'aggressive' : 'reckless';
}

export type PlanName = 'steamroller' | 'matador' | 'anaconda';
export type PlanReadout = 'reckless' | 'fades' | 'safe' | 'balanced';

export interface CornersRead {
  /** 5–98, shown as percentages. */
  finish: number;
  gasTank: number;
  chinExposed: number;
  name: PlanName;
  readout: PlanReadout;
}

/** Style bases: finish, gas tank, chin exposed (placeholder, from the design). */
const BASE: Record<PlanStyle, [number, number, number]> = {
  pressure: [62, 48, 58],
  counter: [44, 70, 36],
  grinder: [38, 62, 30],
};
const NAMES: Record<PlanStyle, PlanName> = { pressure: 'steamroller', counter: 'matador', grinder: 'anaconda' };

/**
 * "Corner's read" (§8): a summary of how the plan tends to go, from the plan and the skills
 * (formula: placeholder). Power and Speed raise the finish rate, Cardio the gas tank, Chin and
 * Guard lower the exposure.
 */
export function cornersRead(plan: GamePlan, ranks: SkillRanks): CornersRead {
  const clamp = (v: number) => Math.max(5, Math.min(98, Math.round(v)));
  const finishes = plan.rounds.filter((r) => r === 'finish').length;
  const pushes = plan.rounds.filter((r) => r === 'push').length;
  const swing = plan.whenHurt === 'swingBack';
  const a = plan.aggression - 50;
  const [f, t, c] = BASE[plan.style];
  const finish = clamp(f + a * 0.5 + finishes * 8 + (swing ? 6 : 0) + (ranks.power + ranks.speed) * 0.8);
  const gasTank = clamp(t - a * 0.4 - pushes * 6 - finishes * 10 + ranks.cardio * 1.5);
  const chinExposed = clamp(c + a * 0.6 + finishes * 6 + (swing ? 14 : -6) - (ranks.chin + ranks.guard) * 1);
  const readout: PlanReadout = chinExposed > 70 ? 'reckless' : gasTank < 35 ? 'fades' : finish < 40 ? 'safe' : 'balanced';
  return { finish, gasTank, chinExposed, name: NAMES[plan.style], readout };
}
